import {
  HeroBanner,
  IMAGE_MIRROR_TOOL,
  ToolCategory,
  UploadButton,
  WithLanding,
} from '@quicktools/shared-components';
import { useTranslation } from 'react-i18next';
import {
  BACKGROUND_REMOVER_TOOL,
  COMPRESS_IMAGE_TOOL,
  RESIZE_IMAGE_TOOL,
} from '@quicktools/shared-components';
import IconWarningCircleOutline from '@picsart/icons/IconWarningCircleOutline';
import { useRouter } from 'next/router';
import { Alert } from '@quicktools/shared-components/components/molecules/Alert';
import HeroFileUploader from '@quicktools/shared-components/components/landingPages/HeroFileUploader';
import { UploadType } from '@quicktools/shared-components/components/core/UploadArea';

import { appConfig } from '../../configs';
import useUploadImage, { UploadErrorType } from '../../hooks/useUploadImage';
import useAppState from '../../store/store';
import analytics from '../../service/analytics/analytics';

import ImageSections from './ImageSections';
import FaqSection from './FaqSection';
import HowToSection from './HowToSection';
import useStyles from './styles';

const SIMILAR_TOOLS = [BACKGROUND_REMOVER_TOOL, RESIZE_IMAGE_TOOL, COMPRESS_IMAGE_TOOL];

function Landing() {
  const { t } = useTranslation();
  const styles = useStyles();
  const { asPath } = useRouter();
  const uploadError = useAppState((state) => state.uploadError);
  const setUploadError = useAppState((state) => state.setUploadError);
  const isProcessing = useAppState((state) => state.isProcessing);
  const { uploadImage } = useUploadImage();

  const handleUpload = (files: File[], type: UploadType | string, pageName: string) => {
    uploadImage(files[0], type, pageName);
  };

  const alertError =
    uploadError === null ? undefined : (
      <Alert
        variant="warning"
        icon={<IconWarningCircleOutline />}
        close={() => setUploadError(null)}
      >
        <div style={{ textAlign: 'left' }}>
          {t(`alerts.${uploadError === UploadErrorType.FILE ? 'error_file' : 'error_general'}`)}{' '}
          <UploadButton
            className={styles.btnTryAgain}
            onChange={(files) => handleUpload(files, 'button', asPath)}
            disabled={isProcessing}
          >
            <span
              style={{
                textTransform: uploadError === UploadErrorType.FILE ? 'capitalize' : 'none',
              }}
            >
              {t('alerts.cta')}
            </span>
          </UploadButton>
          {uploadError === UploadErrorType.FILE ? '' : '.'}
        </div>
      </Alert>
    );

  const heroBannerFooter = (
    <HeroFileUploader
      accept={appConfig.accept}
      multiple={appConfig.multiple}
      onUpload={(files, type) => handleUpload(files, type, asPath)}
      paragraph={t('landing.heroBanner.dragAreaParagraph')}
      btnLabel={t('landing.heroBanner.cta')}
      disableUpload={isProcessing}
      childrenFooter={<div className={styles.heroBannerFooterBtns}></div>}
    />
  );

  return (
    <WithLanding
      analytics={analytics}
      currentTool={IMAGE_MIRROR_TOOL}
      similarTools={SIMILAR_TOOLS}
      toolCategory={ToolCategory.Image}
    >
      <HeroBanner
        heading={t('landing.heroBanner.title')}
        content={t('landing.heroBanner.subtitle')}
        imgSrc={'images/herobanner-pic.png'}
        imgAlt={'appConfig.heroBannerImgAlt'}
        imgWidth={184}
        imgHeight={143}
        partialBg
        alert={alertError}
        footer={heroBannerFooter}
      />

      <ImageSections />

      <HowToSection
        disableUpload={isProcessing}
        onUpload={(files) => handleUpload(files, 'button', asPath)}
      />

      <FaqSection />
    </WithLanding>
  );
}

export default Landing;
